import { debounce } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';

import Button from 'ui/Button';
import Field from 'ui/Field';
import Icon from 'ui/Icon';
import Input from 'ui/Input';
import { Box, VStack } from 'ui/Layout';
import Radio from 'ui/Radio';
import { Text } from 'ui/Typography';

import { AUTOSAVE_DEBOUNCE_MS, springAnimation } from '../../../constants';
import { useApiContext } from '../../../contexts/apiContext';
import { DiscoveredUsThroughEnum } from '../../../generated-api';
import { useCurrentUser } from '../../../utils/user';

type Props = {
  onFinish: () => void;
};

const HowDidYouDiscoverUsStep: React.FC<Props> = ({ onFinish }) => {
  const { user, refetchUser } = useCurrentUser();
  const { api } = useApiContext();

  const [discoveredUsNote, setDiscoveredUsNote] = useState(
    user?.default_team?.discovered_us_note
  );
  const [discoveredUsThrough, setDiscoveredUsThrough] = useState(
    user?.default_team?.discovered_us_through
  );

  const teamID = user?.default_team?.id ?? 0;

  const debouncedSaveNote = useMemo(
    () =>
      debounce((discovered_us_note: string) => {
        api()
          .teams.partialUpdate({
            id: teamID,
            patchedTeamRequest: {
              discovered_us_note,
            },
          })
          .then(refetchUser);
      }, AUTOSAVE_DEBOUNCE_MS),
    []
  );

  const handleNoteChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const discovered_us_note = ev.target.value;
      setDiscoveredUsNote(discovered_us_note);
      debouncedSaveNote(discovered_us_note);
    },
    []
  );

  return (
    <VStack alignItems="stretch" gap={4} mt={2} textAlign="center">
      <Box animation={`${springAnimation} 1000ms linear`}>
        <Icon color="green.800" icon="check-circle" fontSize="7xl" />
      </Box>
      <Text color="green.800" fontSize="2xl">
        You are now subscribed
      </Text>
      <Text color="gray.700" fontSize="sm">
        You can now save your diagrams and access them from anywhere. Would you
        mind sharing <b>how did you discover us?</b>
      </Text>
      <Radio.Group
        value={discoveredUsThrough}
        onValueChange={(e) => {
          const discovered_us_through = e.value as DiscoveredUsThroughEnum;
          setDiscoveredUsThrough(discovered_us_through);
          api().teams.partialUpdate({
            id: teamID,
            patchedTeamRequest: {
              discovered_us_through,
            },
          });
        }}
      >
        <VStack alignItems="flex-start" mt={4}>
          <Radio.Item value={DiscoveredUsThroughEnum.SEARCH}>
            Google search
          </Radio.Item>
          <Radio.Item value={DiscoveredUsThroughEnum.YOUTUBE}>
            YouTube
          </Radio.Item>
          <Radio.Item value={DiscoveredUsThroughEnum.REDDIT}>Reddit</Radio.Item>
          <Radio.Item value={DiscoveredUsThroughEnum.LINKEDIN}>
            LinkedIn
          </Radio.Item>
          <Radio.Item value={DiscoveredUsThroughEnum.TWITTER}>
            Twitter / X
          </Radio.Item>
          <Radio.Item value={DiscoveredUsThroughEnum.RECOMMENDED}>
            Recommended by a friend or colleague
          </Radio.Item>
          <Radio.Item value={DiscoveredUsThroughEnum.OTHER}>Other</Radio.Item>
          {discoveredUsThrough === DiscoveredUsThroughEnum.OTHER && (
            <Field label="In which other way did you discover SankeyArt?">
              <Input
                onChange={handleNoteChange}
                value={discoveredUsNote}
                size="sm"
              />
            </Field>
          )}
        </VStack>
      </Radio.Group>
      <Button
        variant="primary-solid"
        onClick={onFinish}
        disabled={
          !discoveredUsThrough ||
          (discoveredUsThrough === DiscoveredUsThroughEnum.OTHER &&
            !discoveredUsNote)
        }
        mt={6}
      >
        Finish
      </Button>
    </VStack>
  );
};

export default HowDidYouDiscoverUsStep;
